import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import axios from 'src/utils/axios';
import './ComunidadesV2.css';

const API_PREFIX = 'areadoatendente';
const CAN_CREATE_TOPIC = true;

type ViewMode = 'list' | 'feed' | 'post';
type SortMode = 'melhores' | 'recentes';
type AvatarType = 'comunidade' | 'imagem' | 'inicial';

type Community = {
  id: number;
  nome: string;
  descricao: string;
  icone: string;
  cor_fundo: string;
  cor_icone: string;
  texto_meta: string;
  membros_formatado: string;
  total_topicos: number;
};

type Topic = {
  id: number;
  IDComunidade: number;
  tag: string;
  autor: string;
  avatar: string | null;
  avatar_tipo: AvatarType;
  tipo_autor: string;
  is_anjo: boolean;
  you: boolean;
  texto: string;
  tempo: string;
  likes: number;
  liked: boolean;
  comentarios: number;
};

type CommentItem = {
  id: number;
  autor: string;
  avatar: string | null;
  avatar_tipo: AvatarType;
  tipo_autor: string;
  is_anjo: boolean;
  you: boolean;
  texto: string;
  tempo: string;
  likes: number;
  liked: boolean;
  replies: CommentItem[];
};

type ApiError = {
  message?: string;
};

const TAG_STYLES: Record<string, { background: string; color: string }> = {
  Desabafo: { background: '#EDE9FE', color: '#7C5CFC' },
  Pergunta: { background: '#DFF7F0', color: '#0F9D7C' },
  Vitória: { background: '#FCE4EC', color: '#E0578C' },
  'Anjo responde': { background: '#FDE9E0', color: '#F0793C' },
};

const AVATAR_LIBRARY = [
  { id: 'a1', emoji: '🦋', background: '#7C5CFC' },
  { id: 'a2', emoji: '🌙', background: '#3E98FF' },
  { id: 'a3', emoji: '🌻', background: '#F0793C' },
  { id: 'a4', emoji: '🍃', background: '#0F9D7C' },
  { id: 'a5', emoji: '🐢', background: '#0F9D7C' },
  { id: 'a6', emoji: '🦉', background: '#7C5CFC' },
  { id: 'a7', emoji: '🌊', background: '#3E98FF' },
  { id: 'a8', emoji: '⭐', background: '#E0578C' },
  { id: 'a9', emoji: '🔥', background: '#F0793C' },
  { id: 'a10', emoji: '🌈', background: '#E0578C' },
  { id: 'a11', emoji: '🍀', background: '#0F9D7C' },
  { id: 'a12', emoji: '🐦', background: '#3E98FF' },
  { id: 'a13', emoji: '🌸', background: '#E0578C' },
  { id: 'a14', emoji: '🐚', background: '#0F9D7C' },
  { id: 'a15', emoji: '☁️', background: '#3E98FF' },
  { id: 'a16', emoji: '🌱', background: '#F0793C' },
];

const TEXT_EMOJIS = [
  '😊', '🙂', '😢', '😭', '🥹', '😔', '😞', '😌', '🤗', '🙏', '💛', '💪',
  '👏', '✨', '🌱', '🕊️', '😅', '😍', '🥰', '😴', '😬', '😳', '🤔', '❤️',
];

function EmojiPicker({ onSelect }: { onSelect: (emoji: string) => void }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function closePicker(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', closePicker);
    return () => document.removeEventListener('mousedown', closePicker);
  }, []);

  return (
    <div className="comv2-emoji-picker" ref={rootRef}>
      <button
        type="button"
        className="comv2-emoji-trigger"
        onClick={() => setOpen((current) => !current)}
        aria-label="Inserir emoji"
        aria-expanded={open}
      >
        🙂
      </button>
      {open && (
        <div className="comv2-emoji-panel">
          {TEXT_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => {
                onSelect(emoji);
                setOpen(false);
              }}
              aria-label={`Inserir ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function getErrorMessage(error: unknown) {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as ApiError).message);
  }

  return 'Desculpe, aconteceu um erro. Tente novamente.';
}

function countComments(comments: CommentItem[]): number {
  return comments.reduce((total, comment) => total + 1 + countComments(comment.replies), 0);
}

function updateCommentLike(comments: CommentItem[], id: number, liked: boolean, likes: number): CommentItem[] {
  return comments.map((comment) => {
    if (comment.id === id) {
      return { ...comment, liked, likes };
    }

    return { ...comment, replies: updateCommentLike(comment.replies, id, liked, likes) };
  });
}

export default function ComunidadesV2() {
  const [view, setView] = useState<ViewMode>('list');
  const [communities, setCommunities] = useState<Community[]>([]);
  const [selectedCommunity, setSelectedCommunity] = useState<Community | null>(null);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [currentTopic, setCurrentTopic] = useState<Topic | null>(null);
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerText, setComposerText] = useState('');
  const [participateText, setParticipateText] = useState('');
  const [replyOpen, setReplyOpen] = useState<Record<number, boolean>>({});
  const [replyText, setReplyText] = useState<Record<number, string>>({});
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
  const [sortMode, setSortMode] = useState<SortMode>('melhores');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');
  const currentTopicId = currentTopic?.id;

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2400);
  }, []);

  const loadCommunities = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axios.post(`${API_PREFIX}/comunidades-v2`);
      setCommunities(response.data.comunidades || []);
    } catch (error) {
      showToast(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadCommunities();
  }, [loadCommunities]);

  useEffect(() => {
    if (view !== 'post' || !currentTopicId) {
      return undefined;
    }

    let active = true;
    const intervalId = window.setInterval(async () => {
      try {
        const response = await axios.post(`${API_PREFIX}/comunidades-v2/topicos/detalhe`, {
          IDTopico: currentTopicId,
        });

        if (active) {
          setSelectedCommunity(response.data.comunidade);
          setCurrentTopic(response.data.topico);
          setComments(response.data.comentarios || []);
        }
      } catch (error) {
        // Mantém os dados atuais quando uma atualização silenciosa falhar.
      }
    }, 60000);

    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [currentTopicId, view]);

  const sortedComments = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    let list = [...comments];

    if (normalizedSearch) {
      list = list.filter((comment) =>
        `${comment.autor} ${comment.texto}`.toLowerCase().includes(normalizedSearch)
      );
    }

    if (sortMode === 'melhores') {
      return list.sort((a, b) => b.likes - a.likes);
    }

    return list.reverse();
  }, [comments, search, sortMode]);

  async function openFeed(community: Community) {
    setSelectedCommunity(community);
    setView('feed');
    setComposerOpen(false);
    setComposerText('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_PREFIX}/comunidades-v2/topicos`, {
        IDComunidade: community.id,
      });

      setSelectedCommunity(response.data.comunidade || community);
      setTopics(response.data.topicos || []);
    } catch (error) {
      showToast(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  async function createTopic() {
    if (!selectedCommunity || !composerText.trim()) {
      return;
    }

    try {
      const response = await axios.post(`${API_PREFIX}/comunidades-v2/topicos/criar`, {
        IDComunidade: selectedCommunity.id,
        texto: composerText.trim(),
      });

      setTopics((current) => [response.data.topico, ...current]);
      setComposerOpen(false);
      setComposerText('');
    } catch (error) {
      showToast(getErrorMessage(error));
    }
  }

  async function openTopic(topic: Topic) {
    setCurrentTopic(topic);
    setComments([]);
    setSearch('');
    setSortMode('melhores');
    setParticipateText('');
    setView('post');
    setLoading(true);

    try {
      const response = await axios.post(`${API_PREFIX}/comunidades-v2/topicos/detalhe`, {
        IDTopico: topic.id,
      });

      setSelectedCommunity(response.data.comunidade);
      setCurrentTopic(response.data.topico);
      setComments(response.data.comentarios || []);
    } catch (error) {
      showToast(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  async function toggleLike(type: 'topico' | 'comentario', id: number) {
    try {
      const response = await axios.post(`${API_PREFIX}/comunidades-v2/curtir`, {
        tipo: type,
        id,
      });

      if (type === 'topico') {
        setTopics((current) =>
          current.map((topic) =>
            topic.id === id ? { ...topic, liked: response.data.liked, likes: response.data.likes } : topic
          )
        );
        setCurrentTopic((topic) =>
          topic && topic.id === id ? { ...topic, liked: response.data.liked, likes: response.data.likes } : topic
        );
        return;
      }

      setComments((current) => updateCommentLike(current, id, response.data.liked, response.data.likes));
    } catch (error) {
      showToast(getErrorMessage(error));
    }
  }

  async function sendComment(parentId?: number) {
    if (!currentTopic) {
      return;
    }

    const text = parentId ? replyText[parentId] : participateText;

    if (!text || !text.trim()) {
      return;
    }

    try {
      await axios.post(`${API_PREFIX}/comunidades-v2/comentarios/criar`, {
        IDTopico: currentTopic.id,
        IDComentarioPai: parentId || null,
        texto: text.trim(),
      });

      setParticipateText('');
      setReplyText((current) => ({ ...current, [parentId || 0]: '' }));
      setReplyOpen((current) => (parentId ? { ...current, [parentId]: false } : current));
      await openTopic(currentTopic);
    } catch (error) {
      showToast(getErrorMessage(error));
    }
  }

  function renderIcon(name: string, size = 20) {
    return <Icon icon={`lucide:${name || 'users'}`} width={size} height={size} />;
  }

  function renderAvatar(
    author: { autor: string; avatar: string | null; avatar_tipo: AvatarType; is_anjo: boolean },
    className = ''
  ) {
    if (author.avatar_tipo === 'imagem' && author.avatar) {
      return <img className={`comv2-user-avatar ${className}`} src={author.avatar} alt="" />;
    }

    if (author.avatar_tipo === 'comunidade' && author.avatar) {
      const avatar = AVATAR_LIBRARY.find((item) => item.id === author.avatar);

      if (avatar) {
        return (
          <span className={`comv2-user-avatar ${className}`} style={{ backgroundColor: avatar.background }}>
            {avatar.emoji}
          </span>
        );
      }
    }

    return (
      <span
        className={`comv2-user-avatar ${className}`}
        style={{ backgroundColor: author.is_anjo ? '#F0793C' : '#3E98FF' }}
      >
        {author.autor.trim().charAt(0).toUpperCase() || '?'}
      </span>
    );
  }

  function renderCommunityHead() {
    if (!selectedCommunity) {
      return null;
    }

    return (
      <div className="comv2-head">
        <div
          className="comv2-icon-sq"
          style={{ backgroundColor: selectedCommunity.cor_fundo, color: selectedCommunity.cor_icone }}
        >
          {renderIcon(selectedCommunity.icone, 22)}
        </div>
        <div>
          <div className="comv2-head-title">{selectedCommunity.nome}</div>
          <div className="comv2-head-meta">
            {renderIcon('users', 12)}
            <span>
              {selectedCommunity.membros_formatado} pessoas · {selectedCommunity.texto_meta}
            </span>
          </div>
        </div>
      </div>
    );
  }

  function tagStyle(tag: string) {
    return TAG_STYLES[tag] || TAG_STYLES.Desabafo;
  }

  function renderTopic(topic: Topic, detail = false) {
    const style = tagStyle(topic.tag);
    const totalComments = detail ? countComments(comments) : topic.comentarios;
    const interactionProps = detail
      ? {}
      : {
          role: 'button' as const,
          tabIndex: 0,
          onClick: () => openTopic(topic),
          onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openTopic(topic);
            }
          },
        };

    return (
      <div
        key={topic.id}
        className={`${detail ? 'comv2-post-detail' : 'comv2-post comv2-post-clickable'} ${
          topic.is_anjo ? 'comv2-post-angel' : ''
        }`}
        {...interactionProps}
      >
        <div className="comv2-post-top">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="comv2-tag" style={{ backgroundColor: style.background, color: style.color }}>
              {topic.tag}
            </span>
            {topic.is_anjo && <span style={{ color: '#F0793C' }}>{renderIcon('sparkles', 13)}</span>}
          </div>
          <span className="comv2-time">{topic.tempo}</span>
        </div>
        <div className="comv2-post-author-row">
          {renderAvatar(topic, detail ? 'comv2-user-avatar-detail' : '')}
          <div className="comv2-author">{topic.autor}</div>
        </div>
        <p className="comv2-text">{topic.texto}</p>
        <div className="comv2-actions">
          <button
            type="button"
            className={`comv2-action comv2-like-btn ${topic.liked ? 'comv2-action-liked' : ''}`}
            onClick={(event) => {
              event.stopPropagation();
              toggleLike('topico', topic.id);
            }}
          >
            {renderIcon('heart', 16)}
            {topic.likes}
          </button>
          <button
            type="button"
            className="comv2-action"
            onClick={(event) => {
              event.stopPropagation();
              if (!detail) {
                openTopic(topic);
              }
            }}
          >
            {renderIcon('message-circle', 16)}
            {totalComments} {totalComments === 1 ? 'resposta' : 'respostas'}
          </button>
          {detail && (
            <button
              type="button"
              className="comv2-share"
              onClick={() => {
                navigator.clipboard?.writeText(`EYhe · ${topic.autor}: ${topic.texto}`);
                showToast('Link da publicação copiado');
              }}
            >
              {renderIcon('share', 16)}
              Compartilhar
            </button>
          )}
        </div>
      </div>
    );
  }

  function renderComment(comment: CommentItem) {
    const childrenCount = countComments(comment.replies);
    const isCollapsed = collapsed[comment.id];

    return (
      <div className="comv2-thread" key={comment.id}>
        <div className="comv2-comment">
          {renderAvatar(comment, 'comv2-comment-avatar')}
          <div className="comv2-bubble">
            <div className="comv2-comment-author">
              {comment.autor}
              {comment.you && <span className="comv2-you-badge">você</span>}
            </div>
            <div className="comv2-comment-text">{comment.texto}</div>
            <div className="comv2-comment-footer">
              <span className="comv2-time">{comment.tempo}</span>
              <button
                type="button"
                className={`comv2-mini-btn comv2-mini-like-btn ${comment.liked ? 'comv2-mini-liked' : ''}`}
                onClick={() => toggleLike('comentario', comment.id)}
              >
                {renderIcon('heart', 13)} {comment.likes}
              </button>
              <button
                type="button"
                className="comv2-mini-btn"
                onClick={() => setReplyOpen((current) => ({ ...current, [comment.id]: !current[comment.id] }))}
              >
                Responder
              </button>
            </div>

            {replyOpen[comment.id] && (
              <div className="comv2-reply-box">
                <input
                  value={replyText[comment.id] || ''}
                  onChange={(event) => setReplyText((current) => ({ ...current, [comment.id]: event.target.value }))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      sendComment(comment.id);
                    }
                  }}
                  placeholder="Escreva uma resposta com cuidado..."
                />
                <EmojiPicker
                  onSelect={(emoji) =>
                    setReplyText((current) => ({
                      ...current,
                      [comment.id]: `${current[comment.id] || ''}${emoji}`,
                    }))
                  }
                />
                <button type="button" className="comv2-reply-send" onClick={() => sendComment(comment.id)}>
                  Enviar
                </button>
              </div>
            )}

            {comment.replies.length > 0 && (
              <>
                <button
                  type="button"
                  className="comv2-toggle"
                  onClick={() => setCollapsed((current) => ({ ...current, [comment.id]: !current[comment.id] }))}
                >
                  <span>{isCollapsed ? '+' : '-'}</span>
                  {isCollapsed
                    ? `${childrenCount} ${childrenCount === 1 ? 'outra resposta' : 'outras respostas'}`
                    : 'Ocultar respostas'}
                </button>
                {!isCollapsed && (
                  <div className="comv2-thread-children">
                    {comment.replies.map((reply) => renderComment(reply))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (view === 'feed' && selectedCommunity) {
    return (
      <div className="comv2-root">
        <div className="comv2-wrap comv2-wrap-narrow">
          <button type="button" className="comv2-back-btn" onClick={() => setView('list')}>
            {renderIcon('arrow-left', 16)}
            Comunidades
          </button>

          {renderCommunityHead()}

          {CAN_CREATE_TOPIC &&
            (!composerOpen ? (
              <button type="button" className="comv2-new-post" onClick={() => setComposerOpen(true)}>
                Compartilhe o que você está sentindo...
              </button>
            ) : (
              <div className="comv2-composer">
                <textarea
                  value={composerText}
                  onChange={(event) => setComposerText(event.target.value)}
                  placeholder="Compartilhe o que você está sentindo..."
                  maxLength={800}
                />
                <div className="comv2-composer-footer">
                  <div className="comv2-composer-footer-left">
                    <EmojiPicker onSelect={(emoji) => setComposerText((current) => `${current}${emoji}`)} />
                    <span className="comv2-composer-hint">
                      {renderIcon('sparkles', 12)}
                      Publicado como Anjo responde
                    </span>
                  </div>
                  <div className="comv2-composer-buttons">
                    <button
                      type="button"
                      className="comv2-cancel-btn"
                      onClick={() => {
                        setComposerOpen(false);
                        setComposerText('');
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className={`comv2-publish-btn ${composerText.trim() ? 'comv2-publish-active' : ''}`}
                      disabled={!composerText.trim()}
                      onClick={createTopic}
                    >
                      Publicar
                    </button>
                  </div>
                </div>
              </div>
            ))}

          <div className="comv2-feed">
            {loading && <div className="comv2-loading">Carregando...</div>}
            {!loading && topics.length === 0 && <div className="comv2-empty">Nenhum tópico publicado ainda.</div>}
            {topics.map((topic) => renderTopic(topic))}
          </div>

          <div className="comv2-footer-note">
            {renderIcon('shield-check', 13)}
            Todos os nomes aqui são apelidos. Sua identidade é protegida.
          </div>
        </div>
        {toast && <div className="comv2-toast">{toast}</div>}
      </div>
    );
  }

  if (view === 'post' && currentTopic && selectedCommunity) {
    const commentsCount = countComments(comments);

    return (
      <div className="comv2-root">
        <div className="comv2-wrap comv2-wrap-narrow">
          <button type="button" className="comv2-back-btn" onClick={() => openFeed(selectedCommunity)}>
            {renderIcon('arrow-left', 16)}
            Voltar para {selectedCommunity.nome}
          </button>

          {renderTopic(currentTopic, true)}

          <div className="comv2-participate">
            <input
              value={participateText}
              onChange={(event) => setParticipateText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  sendComment();
                }
              }}
              placeholder="Participe da conversa"
            />
            <EmojiPicker onSelect={(emoji) => setParticipateText((current) => `${current}${emoji}`)} />
            <button
              type="button"
              className="comv2-participate-send"
              onClick={() => sendComment()}
              disabled={!participateText.trim()}
            >
              Enviar
            </button>
          </div>

          <div className="comv2-controls">
            <div className="comv2-sort">
              Ordenar por
              <select value={sortMode} onChange={(event) => setSortMode(event.target.value as SortMode)}>
                <option value="melhores">Melhores</option>
                <option value="recentes">Mais recentes</option>
              </select>
            </div>
            <div className="comv2-search">
              {renderIcon('search', 14)}
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar nos comentários"
              />
            </div>
          </div>

          <div className="comv2-heading">
            {commentsCount === 0
              ? 'Nenhuma resposta ainda'
              : `${commentsCount} ${commentsCount === 1 ? 'resposta' : 'respostas'}`}
          </div>
          <div className="comv2-comment-list">
            {loading && <div className="comv2-loading">Carregando...</div>}
            {!loading && sortedComments.length === 0 && (
              <div className="comv2-empty">
                {search ? 'Nenhuma resposta encontrada.' : 'Seja a primeira pessoa a responder com acolhimento.'}
              </div>
            )}
            {sortedComments.map((comment) => renderComment(comment))}
          </div>

          <div className="comv2-footer-note">
            {renderIcon('shield-check', 13)}
            Todos os nomes aqui são apelidos. Sua identidade é protegida.
          </div>
        </div>
        {toast && <div className="comv2-toast">{toast}</div>}
      </div>
    );
  }

  return (
    <div className="comv2-root">
      <div className="comv2-wrap">
        <div className="comv2-hero">
          <h1>
            Encontre gente que entende
            <br />
            por onde você está passando
          </h1>
          <p>Escolha uma comunidade e converse com pessoas vivendo experiências parecidas com a sua.</p>
        </div>

        <div className="comv2-grid">
          {loading && <div className="comv2-loading">Carregando comunidades...</div>}
          {!loading &&
            communities.map((community) => (
              <button
                type="button"
                key={community.id}
                className={`comv2-card ${selectedCommunity?.id === community.id ? 'comv2-card-selected' : ''}`}
                onClick={() => setSelectedCommunity(community)}
              >
                <div
                  className="comv2-icon-sq"
                  style={{ backgroundColor: community.cor_fundo, color: community.cor_icone }}
                >
                  {renderIcon(community.icone)}
                </div>
                <div className="comv2-card-title">{community.nome}</div>
                <div className="comv2-card-desc">{community.descricao}</div>
                <div className="comv2-card-meta">
                  {renderIcon('users', 13)}
                  {community.membros_formatado} pessoas
                </div>
              </button>
            ))}
        </div>

        <div className="comv2-cta-zone">
          <button
            type="button"
            className={`comv2-cta ${selectedCommunity ? 'comv2-cta-active' : ''}`}
            disabled={!selectedCommunity}
            onClick={() => selectedCommunity && openFeed(selectedCommunity)}
          >
            <span>
              {selectedCommunity ? `Entrar na comunidade ${selectedCommunity.nome}` : 'Escolha uma comunidade'}
            </span>
            {renderIcon('arrow-right', 17)}
          </button>
          <div className="comv2-privacy">
            {renderIcon('shield-check', 14)}
            Sua identidade é protegida e as conversas são privadas
          </div>
        </div>
      </div>
      {toast && <div className="comv2-toast">{toast}</div>}
    </div>
  );
}
